import { yupResolver } from "@hookform/resolvers/yup";
import { Stack, Typography } from "@mui/material";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { CheckboxInput } from "Components/Shared/Inputs/Form/CheckboxInput";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { FormPasswordInput } from "Components/Shared/Inputs/Form/FormPasswordInput";
import { Resources, useResource } from "Translations/Resources";
import { type JSX, type Ref, useEffect } from "react";
import { useForm } from "react-hook-form";
import styled from "styled-components";
import {
  type ObjectSchema,
  type StringSchema,
  boolean,
  object,
  string,
} from "yup";

const StyledForm = styled.form`
  margin-top: ${(props) => props.theme.spacing(2)};
`;

const StyledSubmitButton = styled(PrimaryButton)`
  margin-top: ${(props) => props.theme.spacing(3)};
`;

const StyledServerError = styled(Typography)`
  color: ${(props) => props.theme.colors.red};
`;

export type FormData = {
  login: string;
  password?: string;
  agreeWithTermsAndConditions: boolean;
};

type Props = {
  isLoading: boolean;
  serverError: string | null;
  defaultLogin: string | null;
  confirmationCheckboxLabel: string | JSX.Element;
  onSubmit: (formData: FormData) => void;
  onReset: () => void;
  isPasswordless?: boolean;
  emailFieldRef?: Ref<HTMLDivElement>;
  passwordFieldRef?: Ref<HTMLDivElement>;
  disableAutoFocus?: boolean;
};

export const SignUpForm = ({
  isLoading,
  serverError,
  defaultLogin,
  confirmationCheckboxLabel,
  isPasswordless,
  onSubmit,
  emailFieldRef,
  passwordFieldRef,
  disableAutoFocus = false,
}: Props) => {
  const { t } = useResource();

  const validationSchema: ObjectSchema<FormData> = object({
    login: string()
      .email(t(Resources.Validation.InvalidEmail))
      .required(t(Resources.Validation.Required)),
    password: (isPasswordless
      ? string().notRequired()
      : string()
          .required(t(Resources.Validation.Required))
          .min(8, t(Resources.Validation.Password.MinLength, { length: 8 }))
          .matches(/[a-z]/, t(Resources.Validation.Password.MinLowercase))
          .matches(/[A-Z]/, t(Resources.Validation.Password.MinUppercase))
          .matches(
            /[0-9]/,
            t(Resources.Validation.Password.MinNumber),
          )) as StringSchema<string>,
    agreeWithTermsAndConditions: boolean()
      .required()
      .isTrue(t(Resources.Validation.EulaAgreement)),
  }).defined();

  const {
    handleSubmit,
    formState: { errors },
    setError,
    control,
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    mode: "onChange",
    defaultValues: {
      login: defaultLogin ?? "",
      password: "",
      agreeWithTermsAndConditions: false,
    },
  });

  useEffect(() => {
    if (serverError) {
      setError("login", {
        type: "required",
        message: "",
      });
    }
  }, [serverError, setError]);

  return (
    <StyledForm onSubmit={handleSubmit(onSubmit)} autoComplete="off">
      <Stack justifyContent="center">
        <FormInput
          autoFocus={!disableAutoFocus}
          inputRef={emailFieldRef}
          control={control}
          name="login"
          errors={errors}
          label={t(Resources.SignUp.Form.Fields.Login)}
          type="email"
          fullWidth
          valueTransformer={(v) => v.toLowerCase()}
          autoComplete="off"
        />
        {!isPasswordless && (
          <FormPasswordInput
            control={control}
            inputRef={passwordFieldRef}
            name="password"
            label={t(Resources.SignUp.Form.Fields.Password)}
            autoComplete="new-password"
            fullWidth
          />
        )}
        <CheckboxInput
          control={control}
          name="agreeWithTermsAndConditions"
          errors={errors}
          label={confirmationCheckboxLabel}
        />
        <StyledServerError variant="subtitle1">{serverError}</StyledServerError>
        <StyledSubmitButton
          type="submit"
          fullWidth
          variant="contained"
          color="info"
          isLoading={isLoading}
        >
          {t(Resources.SignUp.Form.Controls.SignUpButtonLabel)}
        </StyledSubmitButton>
      </Stack>
    </StyledForm>
  );
};
